/* ============================================
   THE PRINT LOFT — PDP Drawers + Floating Bar JS
   Handles: drawer open/close, focus trap, mobile
   swipe-to-dismiss, floating mini-bar visibility,
   floating bar sold-out state.

   Price rendering is handled by Dawn + Bucks.
   Glazing logic is in tpl-glazing.js.
============================================ */

document.addEventListener('DOMContentLoaded', function () {
  'use strict';

  /* ─── DRAWER LOGIC ─── */

  var backdrop = document.querySelector('[data-drawer-backdrop]');
  var activeDrawer = null;
  var lastFocusedElement = null;

  function closeAllDrawersImmediate() {
    var openDrawers = document.querySelectorAll('.tpl-pdp-drawer.is-open');
    openDrawers.forEach(function (d) {
      d.classList.remove('is-open');
      d.style.transform = '';
      d.setAttribute('hidden', '');
      var t = document.querySelector('[data-drawer-target="' + d.id + '"]');
      if (t) t.setAttribute('aria-expanded', 'false');
    });
    if (backdrop) backdrop.classList.remove('is-active');
    document.body.style.overflow = '';
    activeDrawer = null;
  }

  function openDrawer(id) {
    var drawer = document.getElementById(id);
    if (!drawer) return;

    if (activeDrawer && activeDrawer.id === id) {
      closeDrawer();
      return;
    }

    if (activeDrawer) {
      closeAllDrawersImmediate();
    }

    lastFocusedElement = document.activeElement;
    activeDrawer = drawer;

    drawer.removeAttribute('hidden');
    if (backdrop) backdrop.classList.add('is-active');

    void drawer.offsetHeight;
    drawer.classList.add('is-open');
    springIn(drawer);

    var trigger = document.querySelector('[data-drawer-target="' + id + '"]');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    document.body.style.overflow = 'hidden';

    requestAnimationFrame(function () {
      var closeBtn = drawer.querySelector('[data-drawer-close]');
      if (closeBtn) closeBtn.focus();
    });

    document.addEventListener('keydown', handleEscape);
  }

  function closeDrawer() {
    if (!activeDrawer) return;

    var drawer = activeDrawer;
    activeDrawer = null;

    drawer.classList.remove('is-open');
    if (backdrop) backdrop.classList.remove('is-active');

    var trigger = document.querySelector('[data-drawer-target="' + drawer.id + '"]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');

    document.body.style.overflow = '';
    drawer.style.transform = '';

    var duration = getTransitionDuration(drawer);
    setTimeout(function () {
      drawer.setAttribute('hidden', '');
    }, duration);

    if (lastFocusedElement) {
      lastFocusedElement.focus();
      lastFocusedElement = null;
    }

    document.removeEventListener('keydown', handleEscape);
  }

  function getTransitionDuration(el) {
    var style = getComputedStyle(el);
    var dur = parseFloat(style.transitionDuration) || 0;
    return dur * 1000;
  }

  function handleEscape(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeDrawer();
    }
  }

  function trapFocus(e) {
    if (!activeDrawer || e.key !== 'Tab') return;

    var focusable = activeDrawer.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  document.addEventListener('keydown', trapFocus);

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-drawer-target]');
    if (trigger) {
      e.preventDefault();
      openDrawer(trigger.getAttribute('data-drawer-target'));
      return;
    }

    var closeBtn = e.target.closest('[data-drawer-close]');
    if (closeBtn) {
      e.preventDefault();
      closeDrawer();
      return;
    }

    /* Backdrop click → close (Pass 3.4). closeDrawer already returns focus
       to lastFocusedElement at :89-92, so accessibility is preserved. */
    if (activeDrawer && backdrop && e.target === backdrop) {
      e.preventDefault();
      closeDrawer();
      return;
    }

    /* Description "See more/less" toggle — event delegation replaces
       inline onclick handler for CSP-readiness (Apr 17 Session C2). */
    var descToggle = e.target.closest('[data-tpl-description-toggle]');
    if (descToggle) {
      var target = descToggle.previousElementSibling;
      if (target) {
        var expanded = target.classList.toggle('is-expanded');
        descToggle.setAttribute('aria-expanded', String(expanded));
        var more = descToggle.getAttribute('data-tpl-label-more') || 'See more';
        var less = descToggle.getAttribute('data-tpl-label-less') || 'See less';
        descToggle.textContent = expanded ? less : more;
      }
      return;
    }

    if (activeDrawer) {
      var clickedInsideDrawer = e.target.closest('.tpl-pdp-drawer');
      if (!clickedInsideDrawer) {
        closeDrawer();
      }
    }
  });


  /* ─── MOBILE SWIPE-TO-DISMISS ─── */

  var touchStartY = 0;
  var touchCurrentY = 0;
  var isDragging = false;
  var SWIPE_THRESHOLD = 80;
  var FLING_VELOCITY = 0.6;      /* px per ms, downward */
  var lastTouchY = 0, lastTouchT = 0, touchVelocity = 0;
  var SHEET_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }
  function isMobile() { return window.innerWidth < 750; }

  /* Bottom sheet (motion.dev "Sheet modal" port, 2026-09-07): damped spring
     sampled into keyframes; overrides the CSS transition while it runs. */
  function springFrames(fromPx, stiffness, damping, durationMs) {
    var w0 = Math.sqrt(stiffness), zeta = damping / (2 * w0), steps = 40, frames = [];
    for (var i = 0; i <= steps; i++) {
      var t = (i / steps) * (durationMs / 1000);
      var wd = w0 * Math.sqrt(1 - zeta * zeta);
      var x = 1 - Math.exp(-zeta * w0 * t) * (Math.cos(wd * t) + (zeta * w0 / wd) * Math.sin(wd * t));
      frames.push({ transform: 'translateY(' + (fromPx * (1 - x)).toFixed(2) + 'px)' });
    }
    return frames;
  }
  function springIn(drawer) {
    if (!isMobile() || reducedMotion() || typeof drawer.animate !== 'function') return;
    drawer.animate(springFrames(drawer.offsetHeight || 400, 320, 26, 650), { duration: 650, easing: 'linear' });
  }
  function springBack(drawer, fromPx) {
    if (reducedMotion() || typeof drawer.animate !== 'function') { drawer.style.transform = ''; return; }
    drawer.style.transform = '';
    drawer.animate(springFrames(fromPx, 320, 26, 600), { duration: 600, easing: 'linear' });
  }
  function setBackdropProgress(p) {
    if (!backdrop) return;
    backdrop.style.background = 'rgba(0, 0, 0, ' + (0.3 * Math.max(0, Math.min(1, p))).toFixed(3) + ')';
  }

  document.addEventListener('touchstart', function (e) {
    if (!activeDrawer) return;
    var handle = activeDrawer.querySelector('.tpl-pdp-drawer__header');
    var body = activeDrawer.querySelector('.tpl-pdp-drawer__body');
    var onHandle = handle && handle.contains(e.target);
    var onBodyTop = body && body.contains(e.target) && activeDrawer.scrollTop <= 0;
    if (!onHandle && !onBodyTop) return;

    touchStartY = e.touches[0].clientY;
    touchCurrentY = touchStartY;
    lastTouchY = touchStartY; lastTouchT = e.timeStamp; touchVelocity = 0;
    isDragging = true;
    activeDrawer.classList.add('is-dragging');
    activeDrawer.getAnimations && activeDrawer.getAnimations().forEach(function (a) { a.cancel(); });
  }, { passive: true });

  document.addEventListener('touchmove', function (e) {
    if (!isDragging || !activeDrawer) return;

    touchCurrentY = e.touches[0].clientY;
    var dt = Math.max(1, e.timeStamp - lastTouchT);
    touchVelocity = (touchCurrentY - lastTouchY) / dt;
    lastTouchY = touchCurrentY; lastTouchT = e.timeStamp;
    var deltaY = touchCurrentY - touchStartY;

    if (!isMobile()) return;
    if (deltaY > 0) {
      activeDrawer.style.transform = 'translateY(' + deltaY + 'px)';
      setBackdropProgress(1 - deltaY / (activeDrawer.offsetHeight || 400));
    } else {
      /* rubber band when pulled up */
      activeDrawer.style.transform = 'translateY(' + (deltaY * 0.12) + 'px)';
    }
  }, { passive: true });

  document.addEventListener('touchend', function () {
    if (!isDragging || !activeDrawer) return;
    isDragging = false;

    var drawer = activeDrawer;
    drawer.classList.remove('is-dragging');
    var deltaY = touchCurrentY - touchStartY;
    if (backdrop) backdrop.style.background = '';

    if (isMobile() && (deltaY > SWIPE_THRESHOLD || (deltaY > 12 && touchVelocity > FLING_VELOCITY))) {
      closeDrawer();
    } else {
      springBack(drawer, deltaY);
    }

    touchStartY = 0;
    touchCurrentY = 0;
    touchVelocity = 0;
  }, { passive: true });


  /* ─── TOP STRIP SPEED (2026-09-06) ───
     The strip animates translateX(-50%) over a fixed duration, so its px/s
     scales with the run width (icons + four extra chips doubled it, and
     intl visitors hide two items). Derive the duration from the measured
     half-track so it always drifts at the calibrated 11.7 px/s; re-run
     after load and once the market toggle has settled. */
  var topstripTrack = document.querySelector('.tpl-pdp-topstrip__track');
  if (topstripTrack) {
    var STRIP_PX_PER_S = 11.7;
    var tuneStrip = function () {
      var half = topstripTrack.scrollWidth / 2;
      if (half > 0) topstripTrack.style.animationDuration = Math.round(half / STRIP_PX_PER_S) + 's';
    };
    tuneStrip();
    window.addEventListener('load', tuneStrip, { once: true });
    setTimeout(tuneStrip, 2500);
  }

  /* ─── FLOATING MINI-BAR ─── */

  var floatingBar = document.getElementById('tpl-floating-bar');
  var floatingAddBtn = document.getElementById('tpl-floating-add');

  /* Stable lookup for the main ATC button. Prefer the explicit data-attribute
     emitted by buy-buttons.liquid; fall back to legacy class/name selectors so
     other Dawn-based forms on the page (quick-add, featured-product) still match. */
  function findMainCTA() {
    return document.querySelector('[data-tpl-main-cta]')
        || document.querySelector('.product-form__submit')
        || document.querySelector('button[name="add"]');
  }

  if (floatingBar) {
    var mainCTA = findMainCTA();

    if (mainCTA && 'IntersectionObserver' in window) {
      var hasScrolled = false;
      window.addEventListener(
        'scroll',
        function () {
          hasScrolled = true;
        },
        { once: true, passive: true }
      );

      /* The bar and the Archivist card are both position:fixed bottom:0, and
         the card sits at --z-float (9998) against the bar's 998 — while the
         card is open it covers Add to Cart outright. Mirror the bar's state
         onto <html> so the card can yield the bottom edge; the rule lives in
         snippets/tpl-archivist-card.liquid. */
      var barWasVisible = false;
      var setBarVisible = function (visible) {
        floatingBar.classList.toggle('is-visible', visible);
        floatingBar.setAttribute('aria-hidden', visible ? 'false' : 'true');
        document.documentElement.classList.toggle('tpl-bar-on', visible);
        /* Sticky bar morph (motion.dev "Shared layout" port, 2026-09-08): the
           island springs up and its button pops as the main CTA scrolls away. */
        if (visible && !barWasVisible && !reducedMotion() && typeof floatingBar.animate === 'function') {
          floatingBar.animate(springFrames(floatingBar.offsetHeight + 22, 320, 22, 650), { duration: 650, easing: 'linear' });
          var cta = floatingBar.querySelector('.tpl-pdp-floating-bar__cta');
          if (cta) cta.animate([{ transform: 'scale(0.86)' }, { transform: 'scale(1.04)', offset: 0.6 }, { transform: 'scale(1)' }], { duration: 520, delay: 120, easing: SHEET_EASE });
        }
        barWasVisible = visible;
      };

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            /* Only once the main CTA has been scrolled PAST (its top is above
               the viewport). A bare !isIntersecting also fires while the CTA is
               still below the fold, which would pop the bar up before the
               visitor has reached the buy control and would hide the Archivist
               card on the very first scroll. */
            var scrolledPastCTA = entry.boundingClientRect.top < 0;
            setBarVisible(hasScrolled && !entry.isIntersecting && scrolledPastCTA);
          });
        },
        { threshold: 0, rootMargin: '0px' }
      );

      observer.observe(mainCTA);
    }

    if (floatingAddBtn) {
      floatingAddBtn.addEventListener('click', function () {
        /* 2026-09-06: while the main CTA is busy or unavailable the floating
           button mirrors that state, so a second tap must not re-fire it. */
        if (floatingAddBtn.disabled) return;
        var btn = findMainCTA();
        if (btn) btn.click();
      });

      /* 2026-09-06: the floating button used to sit at "ADD TO CART" through
         the whole add, so the only feedback a scrolled-down visitor got was
         the drawer opening. Mirror the main CTA's disabled state and its
         Adding/Added text onto it. Sold-out text is owned by the variant
         handler below; only restore the add text while the main CTA is live. */
      var mirrorCTA = findMainCTA();
      if (mirrorCTA && 'MutationObserver' in window) {
        var ADDING_RE = /adding/i;
        var ADDED_RE = /added/i;

        var mirrorState = function () {
          var label = (mirrorCTA.querySelector('.btn-text') || mirrorCTA).textContent || '';
          var busy = !!mirrorCTA.disabled;

          floatingAddBtn.disabled = busy;

          if (ADDING_RE.test(label)) {
            /* Same three dots the cart bridge writes on the main button. */
            floatingAddBtn.textContent = 'ADDING...';
          } else if (ADDED_RE.test(label)) {
            floatingAddBtn.textContent = 'ADDED ✓';
          } else if (!busy) {
            floatingAddBtn.textContent =
              floatingAddBtn.getAttribute('data-add-text') || 'ADD TO CART';
          }
          /* Disabled with neither label means the variant is unavailable:
             the variantChange handler below owns data-sold-text there, so
             leave the label alone rather than fighting it. */
        };

        new MutationObserver(mirrorState).observe(mirrorCTA, {
          attributes: true,
          attributeFilter: ['disabled', 'class'],
          childList: true,
          characterData: true,
          subtree: true
        });
      }
    }
  }


  /* ─── VARIANT CHANGE: CTA TEXT + SOLD-OUT STATE ───
     G-1: order of pub/sub subscribers is set by script load order in
     main-product.liquid (all `defer`, so DOM order applies in spec-compliant
     browsers). This handler restores the brand CTA text ("ACQUIRE THIS WORK"
     etc.) AFTER Dawn's toggleAddButton resets it to window.variantStrings.addToCart
     — works only because Dawn's handler is registered earlier. If load order
     changes, reset → restore order can flip and CTA text reverts to translation
     strings.

     Actual `<script defer>` order in sections/main-product.liquid:48-53:
       48. product-info.js — quantity rules
       49. product-form.js — Dawn ATC handler (mostly bypassed by cart bridge)
       50. tpl-product-page.js — edition counter + hover zoom (publishes? no, subscribes)
       51. tpl-pdp-drawers.js (here) — drawers + CTA text restore
       52. tpl-glazing.js — visibility + price
       53. tpl-frame-picker.js — raw document `variant:change` events, not pub/sub

     Dawn's VariantSelects.renderProductInfo() is the publisher of variantChange.
     It loads earlier via global.js (theme.liquid head), so by the time the above
     scripts subscribe, it's already wired up. */

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      var variant = event.data && event.data.variant;
      if (!variant) return;

      /* Floating bar button */
      if (floatingAddBtn) {
        var btn = document.getElementById('tpl-floating-add') || floatingAddBtn;
        var addText = btn.getAttribute('data-add-text') || 'ADD TO CART';
        var soldText = btn.getAttribute('data-sold-text') || 'SOLD OUT';

        btn.disabled = !variant.available;
        btn.textContent = variant.available ? addText : soldText;
      }

      /* Main CTA — Dawn's toggleAddButton resets text to variantStrings.addToCart.
         Restore from data-add-text if present (set on every product after D-4). */
      var mainBtn = findMainCTA();
      if (mainBtn) {
        var mainAddText = mainBtn.getAttribute('data-add-text');
        if (mainAddText && variant.available) {
          /* Prefer the .btn-text span (set after D-2) so we don't clobber the
             loading-spinner SVG sibling. Fall back to the bare span for any
             non-PDP form that hasn't adopted .btn-text yet. */
          var span = mainBtn.querySelector('.btn-text') || mainBtn.querySelector('span');
          if (span) span.textContent = mainAddText;
        }
      }

    });
  }

  /* =====================================================================
     TPL · 2026-05-03 sizing-drawer carousel
     Tracks the active slide in the "In real homes" carousel via
     IntersectionObserver and toggles `.is-active` on the matching dot
     plus updates the "n / total" counter. Fail-open: if JS doesn't run
     the first dot stays highlighted (rendered with `.is-active` from
     Liquid), and the counter shows "1 / N" by default.
     ===================================================================== */
  (function initSizeCarousels() {
    if (!('IntersectionObserver' in window)) return;
    var carousels = document.querySelectorAll('[data-tpl-size-carousel]');
    if (!carousels.length) return;

    carousels.forEach(function (carousel) {
      var slides = carousel.querySelectorAll('[data-tpl-carousel-slide]');
      var dots = carousel.querySelectorAll('[data-tpl-carousel-dot]');
      var counter = carousel.querySelector('[data-tpl-carousel-count]');
      var track = carousel.querySelector('[data-tpl-carousel-track]');
      if (!slides.length || !track) return;

      function setActive(idx) {
        dots.forEach(function (dot, i) {
          dot.classList.toggle('is-active', i === idx);
        });
        if (counter) counter.textContent = (idx + 1) + ' / ' + slides.length;
      }

      var observer = new IntersectionObserver(function (entries) {
        /* Pick the entry with the highest intersection ratio that's currently
           intersecting — that's the slide most-visible to the user. */
        var top = null;
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          if (!top || entry.intersectionRatio > top.intersectionRatio) top = entry;
        });
        if (!top) return;
        var idx = parseInt(top.target.getAttribute('data-slide-index'), 10) || 0;
        setActive(idx);
      }, {
        root: track,
        threshold: [0.5, 0.75, 1]
      });

      slides.forEach(function (slide) { observer.observe(slide); });
    });
  })();

  /* Star badge in the title meta row scrolls to the reviews.
     Clarity showed repeated taps on the stars with nothing bound to them.
     2026-09-06: prefer our own review rail (.tpl-pr), which is the section
     that actually renders reviews on the PDP now; the Judge.me widget stays
     as fallback. Both resolve at click time (Judge.me loads async and
     exposes no stable container id) and no-op if neither has rendered. */
  (function () {
    var jump = document.querySelector('[data-tpl-reviews-jump]');
    if (!jump) return;

    jump.addEventListener('click', function () {
      var widget = document.querySelector('.tpl-pr') || document.querySelector('.jdgm-widget');
      if (!widget) return;
      widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  })();

});
